import { Component, OnInit, ViewChild, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { Vendor } from '@models/vendor';
import { VendorOrders } from '@models/vendor-order';
import { OrderService } from '@services/order.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { RX_STATUS_OPTIONS } from '../../../../../constants/rx-status';

@Component({
  selector: 'app-stack-vendors',
  templateUrl: './stack-vendors.component.html',
  styleUrls: ['../../managed-board.component.css', './stack-vendors.component.css'],
  animations: [
  trigger('detailExpand', [
    state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
    state('expanded', style({ height: '*', visibility: 'visible' })),
    transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class StackVendorsComponent implements OnInit {
  @Input() public section: string = 'orders';
  @Input() public tableView: string = '';
  @Output() countsChange = new EventEmitter<{ total: number; customer: number }>();
  @Output() jobsChanged = new EventEmitter<void>();
  @ViewChild(MatSort, {static: false}) sort: MatSort;
  dataSource: MatTableDataSource<VendorOrders> = new MatTableDataSource<VendorOrders>();
  displayedColumns = ['alert', 'vendor', 'count'];
	expandedElement = null;
  validateEmail = Vendor.validateEmail;
  validateUrl = Vendor.validateUrl;
  dataLoading: boolean = false;
  flatRxVendorOrders: VendorOrders = new VendorOrders();

  get isFlatList(): boolean {
    return (
      this.section === 'awaiting-rx' ||
      this.section === 'call-for-rx' ||
      this.section === 'hold' ||
      this.section === 'to-repair' ||
      this.section === 'awaiting-frames' ||
      this.section === 'awaiting-repair'
    );
  }

  get resolvedTableView(): string {
    if (this.tableView) {
      return this.tableView;
    }
    if (this.section === 'hold') {
      return 'hold';
    }
    if (this.section === 'to-repair') {
      return 'orders';
    }
    if (this.section === 'awaiting-frames' || this.section === 'awaiting-repair') {
      return 'awaiting';
    }
    return 'orders';
  }

  constructor(private orderService: OrderService, private cdRef:ChangeDetectorRef, private route: ActivatedRoute) {}

  ngOnInit() {
  	this.getNewOrders();
  }

  getNewOrders() {
    this.dataLoading = true;
    this.orderService.getNewOrders(this.section).subscribe(vendorOrders => {
      vendorOrders = this.filterVendorOrdersForSection(vendorOrders);
      const total = vendorOrders.reduce(
        (sum, vendorOrder) => sum + (vendorOrder.orders || []).length,
        0
      );
      const customer = vendorOrders.reduce(
        (sum, vendorOrder) =>
          sum + (vendorOrder.orders || []).filter((order) => this.isCustomerOrder(order)).length,
        0
      );
      this.countsChange.emit({ total, customer });

      if (this.isFlatList) {
        this.flatRxVendorOrders = this.buildFlatVendorOrders(vendorOrders);
      } else {
        this.route.paramMap.subscribe(params => {
          let vendorId = params.get('vendorId');
          this.expandedElement = vendorOrders.find(x => x.vendor._id === vendorId);
        });

        this.dataSource = new MatTableDataSource<VendorOrders>(vendorOrders);
        this.dataSource.sortingDataAccessor = (item, property) => {
          switch(property) {
            case 'vendor':
            default: return item[property];
          }
        };
        this.dataSource.sort = this.sort;
      }
      this.dataLoading = false;
      this.cdRef.detectChanges(); 
    });
	}

	toggleExpanded(element, index) {
    if (this.expandedElement !== element) {
      this.expandedElement = element;
  		this.displayedColumns = ['alert', 'vendor', 'count', 'accountNumber', 'repName', 'orderProtocol', 'orderInfo'];
    } else {
      this.expandedElement = null;
  		this.displayedColumns = ['alert', 'vendor', 'count'];
    }
    this.cdRef.detectChanges(); 
  }

  onChildSaved(): void {
    this.getNewOrders();
    this.jobsChanged.emit();
  }

  private buildFlatVendorOrders(vendorOrders: VendorOrders[]): VendorOrders {
    const flatVendorOrders = new VendorOrders();
    flatVendorOrders.orders = vendorOrders.flatMap(
      (vendorOrder) => vendorOrder.orders || []
    );
    return flatVendorOrders;
  }

  private isCustomerOrder(order: any): boolean {
    const source = String(order?.products?.[0]?.frame?.frameSource || '').trim();
    return !!source && source !== 'STOCK';
  }

  private filterVendorOrdersForSection(
    vendorOrders: VendorOrders[]
  ): VendorOrders[] {
    const requiredRxStatus = this.getRequiredRxStatus();
    if (!requiredRxStatus) {
      return vendorOrders;
    }

    return vendorOrders
      .map((vendorOrder) => ({
        ...vendorOrder,
        orders: (vendorOrder.orders || []).filter(
          (order) => order.rxStatus === requiredRxStatus
        ),
      }))
      .filter((vendorOrder) => vendorOrder.orders.length > 0);
  }

  private getRequiredRxStatus(): string | null {
    switch (this.section) {
      case 'awaiting-rx':
        return RX_STATUS_OPTIONS[0].value;
      default:
        return null;
    }
  }

}
